/**
 * Reading `TimeOff` rows on the owner's screens: what a block says, and whether
 * it stands in the way of a booking.
 *
 * A block is ONE continuous span in salon wall-clock time — from `startTime` on
 * `startDate` (midnight if null) through `endTime` on `endDate` (the end of that
 * day if null, since `endDate` is inclusive). That is `time_off_window` on the
 * server; the two have to agree or a warning shown before submit will contradict
 * the error returned after it.
 */
import { format, parseISO } from 'date-fns';
import type { Service, Staff, TimeOff } from '../api';

/** "Mon 10 Aug" from a plain yyyy-MM-dd, with no timezone shift. */
export function formatSalonDay(isoDate: string): string {
  return format(parseISO(isoDate), 'EEE d MMM');
}

/**
 * Who a block is about. A worker being away is not a closure — the salon is
 * open, and saying "Salon closed" over one worker's dentist appointment would
 * have the owner turning away business they can serve.
 */
export function timeOffSubject(block: TimeOff): string {
  // staffName comes from the same row as staffId, but an inactive worker can
  // still own blocks; fall back rather than render "undefined away".
  return block.staffId === null ? 'Salon closed' : `${block.staffName || 'A worker'} away`;
}

/**
 * The block's span in words: "Fri 21 Aug", "Fri 21 Aug, 09:00–12:00",
 * "Mon 10 Aug 14:00 → Wed 12 Aug 09:00".
 *
 * A multi-day block is one continuous span, not a daily repeat, so its times
 * bound the two ends of the range rather than each day inside it. Each time is
 * independently optional, which is what "from 14:00" and "until 09:00" cover.
 */
export function formatTimeOffSpan(block: TimeOff): string {
  const from = formatSalonDay(block.startDate);
  if (block.startDate === block.endDate) {
    if (block.startTime && block.endTime) return `${from}, ${block.startTime}–${block.endTime}`;
    if (block.startTime) return `${from}, from ${block.startTime}`;
    if (block.endTime) return `${from}, until ${block.endTime}`;
    return from;
  }
  const to = formatSalonDay(block.endDate);
  const opens = block.startTime ? `${from} ${block.startTime}` : from;
  const closes = block.endTime ? `${to} ${block.endTime}` : to;
  return `${opens} → ${closes}`;
}

const MINUTES_PER_DAY = 1440;

/**
 * A yyyy-MM-dd plus an optional "HH:mm", as one comparable number of minutes.
 *
 * Via `Date.UTC` on purpose: every value compared here is already salon
 * wall-clock on both sides, and UTC has no DST, so an hour can't appear or
 * vanish between a block's bounds and a booking's. `fallback` is what a null
 * time means — 0 for a start (midnight), a full day for an end.
 */
function wallClockMinutes(isoDate: string, time: string | null, fallback: number): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  const midnight = Date.UTC(y, m - 1, d) / 60_000;
  if (!time) return midnight + fallback;
  const [hours, minutes] = time.split(':').map(Number);
  return midnight + hours * 60 + minutes;
}

type Window = [start: number, end: number];

/**
 * Whether `block` covers any of `slot`, judged the way the server judges it.
 *
 * `dayStart` is midnight on the booking's start date, and the block is clipped
 * to that one day before the comparison — which is exactly what the server does
 * (`check_salon_time_off` looks up `day_time_off` for the start date alone, and
 * `time_off_intervals` clips each row to it). Comparing the full spans instead
 * would warn about a booking that runs past midnight into the next day's
 * closure, and the server would then accept it — a warning contradicting the
 * save is worse than no warning.
 *
 * Half-open on both sides, as `slot_is_free` is: a booking that starts exactly
 * when a block ends does not collide with it. A block that clips to nothing
 * covers nothing, which is how the server drops it too.
 */
function coversSlot(block: TimeOff, slot: Window, dayStart: number): boolean {
  const start = Math.max(wallClockMinutes(block.startDate, block.startTime, 0), dayStart);
  const end = Math.min(
    wallClockMinutes(block.endDate, block.endTime, MINUTES_PER_DAY),
    dayStart + MINUTES_PER_DAY,
  );
  if (end <= start) return false;
  // An empty slot is the "no service picked yet" probe below: ask whether the
  // instant itself falls inside the block, which `slot[0] < slot[1]` cannot.
  if (slot[0] === slot[1]) return start <= slot[0] && slot[0] < end;
  return start < slot[1] && slot[0] < end;
}

// The server's own wording, so the pre-check and the error that follows a failed
// submit can't say different things. Mirrors `time_off_detail(allow_overlap=True)`
// — the owner branch, which names the block rather than hiding it.
const SALON_WIDE_MESSAGE =
  "You're marked off at that time. Shorten or remove the time off to book it.";
const NAMED_WORKER_MESSAGE = 'This worker is on time off at that time.';
const EVERY_WORKER_MESSAGE = 'Every worker who offers this service is on time off at that time.';

export interface TimeOffClash {
  message: string;
  /** The block behind it, so the owner is told which one to go and shorten. */
  block: TimeOff;
}

/**
 * The time off that would make this booking fail, or null if none would.
 *
 * An approximation of the server's check, deliberately narrowed to time off: it
 * knows nothing about business hours or existing bookings, so a null here means
 * "no *absence* is in the way", never "this will save". Treat it as a warning
 * and let the API have the last word — see the call site.
 *
 * `service` is optional because the modal opens with none chosen: the answer has
 * to hold from the moment a slot is clicked, or the warning arrives after the
 * typing it was meant to save. Without one there is no duration, so only the
 * start instant is judged — which is exactly the "clicked into a closure" case.
 */
export function findTimeOffClash(options: {
  rows: TimeOff[];
  /** The booking's start as "yyyy-MM-ddTHH:mm" in salon wall-clock time. */
  startsAt: string;
  service: Service | undefined;
  /** The chosen worker, or null for "any available". */
  staffId: number | null;
  /** Active workers. Empty at a salon that books at salon level. */
  staff: Staff[];
}): TimeOffClash | null {
  const { rows, startsAt, service, staffId, staff } = options;
  const [date, time] = startsAt.split('T');
  if (!date || !time) return null;

  const dayStart = wallClockMinutes(date, null, 0);
  const start = wallClockMinutes(date, time.slice(0, 5), 0);
  const slot: Window = [start, start + (service?.duration ?? 0)];
  const hits = rows.filter((row) => coversSlot(row, slot, dayStart));
  if (hits.length === 0) return null;

  // Salon-wide first: it closes everyone, including every worker, so it is both
  // the truest thing to say and the only kind that means anything at a salon
  // with no workers.
  const salonWide = hits.find((row) => row.staffId === null);
  if (salonWide) return { message: SALON_WIDE_MESSAGE, block: salonWide };
  if (staff.length === 0) return null;

  if (staffId !== null) {
    const theirs = hits.find((row) => row.staffId === staffId);
    return theirs ? { message: NAMED_WORKER_MESSAGE, block: theirs } : null;
  }

  // "Any available": one free worker and the server simply assigns them, so this
  // only fails when every worker who could take the booking is away. Which
  // workers those are is a property of the service, so this is the one branch
  // that has to wait for it to be picked.
  if (!service) return null;
  const candidates = staff.filter((worker) => worker.serviceIds.includes(service.id));
  const blocking = candidates.map((worker) => hits.find((row) => row.staffId === worker.id));
  const first = blocking[0];
  // No candidates at all isn't a time-off problem, and the server says something
  // more useful about it than this could.
  if (!first || blocking.some((row) => !row)) return null;
  return { message: EVERY_WORKER_MESSAGE, block: first };
}
